/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import {
  IoCloseCircleSharp,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoCloudUploadOutline,
  IoColorWandOutline,
  IoAddCircleOutline,
  IoTrashOutline,
} from 'react-icons/io5'
import {
  FiSliders,
  FiTag,
  FiHash,
  FiDollarSign,
  FiPackage,
  FiUser,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiLock,
} from 'react-icons/fi'
import { toast } from 'react-toastify'
import api from '../../../services/axios'

interface Props {
  isOpen: boolean
  onClose: () => void
  tipoInicial?: 'vendas' | 'estoque' | 'produtos' | 'forcavenda' | 'clientes'
  distId?: number
  layoutsCadastrados?: Record<string, any[]>
  onApplyLayout: (tipo: string, camposMapeados: any[]) => void
}

export const LAYOUTS_PADRAO: Record<string, { campo: string; label: string; inicio: number; tamanho: number }[]> = {
  vendas: [
    { campo: 'cnpj', label: 'CNPJ do Distribuidor', inicio: 1, tamanho: 14 },
    { campo: 'indetificador_do_cliente', label: 'Identificador do Cliente', inicio: 15, tamanho: 14 },
    { campo: 'data', label: 'Data da Venda (YYYYMMDD)', inicio: 33, tamanho: 8 },
    { campo: 'numero_transacao', label: 'Número do Documento / NF', inicio: 41, tamanho: 19 },
    { campo: 'cod_produto', label: 'Código do Produto / EAN', inicio: 61, tamanho: 14 },
    { campo: 'quantidade', label: 'Quantidade', inicio: 74, tamanho: 18 },
    { campo: 'preco', label: 'Valor / Preço', inicio: 92, tamanho: 10 },
    { campo: 'cod_vendedor', label: 'Código do Vendedor', inicio: 103, tamanho: 30 },
    { campo: 'tipo_venda', label: 'Tipo de Venda / Documento', inicio: 133, tamanho: 1 },
  ],
  estoque: [
    { campo: 'cnpj', label: 'CNPJ do Distribuidor', inicio: 1, tamanho: 14 },
    { campo: 'cod_produto', label: 'Código do Produto / EAN', inicio: 15, tamanho: 14 },
    { campo: 'quantidade', label: 'Quantidade em Estoque', inicio: 29, tamanho: 22 },
  ],
  produtos: [
    { campo: 'cnpj_fornecedor', label: 'CNPJ Fornecedor', inicio: 1, tamanho: 14 },
    { campo: 'razao_social', label: 'Razão Social Fornecedor', inicio: 19, tamanho: 30 },
    { campo: 'cod_produto', label: 'Código do Produto', inicio: 64, tamanho: 14 },
    { campo: 'nm_produto', label: 'Nome / Descrição do Produto', inicio: 79, tamanho: 71 },
    { campo: 'divisao', label: 'Categoria / Divisão', inicio: 179, tamanho: 31 },
    { campo: 'status', label: 'Status do Produto', inicio: 249, tamanho: 2 },
  ],
  forcavenda: [
    { campo: 'cnpj', label: 'CNPJ do Distribuidor', inicio: 1, tamanho: 14 },
    { campo: 'cliente', label: 'Código do Cliente', inicio: 15, tamanho: 18 },
    { campo: 'cod_gerente', label: 'Código do Gerente', inicio: 33, tamanho: 13 },
    { campo: 'nm_gerente', label: 'Nome do Gerente', inicio: 46, tamanho: 50 },
    { campo: 'cod_supervisor', label: 'Código do Supervisor', inicio: 96, tamanho: 13 },
    { campo: 'nm_supervisor', label: 'Nome do Supervisor', inicio: 109, tamanho: 50 },
    { campo: 'cod_vendedor', label: 'Código do Vendedor', inicio: 159, tamanho: 20 },
    { campo: 'nm_vendedor', label: 'Nome do Vendedor', inicio: 179, tamanho: 52 },
  ],
  clientes: [
    { campo: 'cnpj', label: 'CNPJ do Distribuidor', inicio: 1, tamanho: 14 },
    { campo: 'indetificador_do_cliente', label: 'Código / CNPJ do Cliente', inicio: 15, tamanho: 15 },
    { campo: 'razao_social', label: 'Razão Social / Nome', inicio: 30, tamanho: 43 },
    { campo: 'endereco', label: 'Endereço', inicio: 73, tamanho: 40 },
    { campo: 'bairro', label: 'Bairro', inicio: 113, tamanho: 30 },
    { campo: 'cep', label: 'CEP', inicio: 143, tamanho: 9 },
    { campo: 'cidade', label: 'Cidade', inicio: 152, tamanho: 30 },
    { campo: 'estado', label: 'UF / Estado', inicio: 182, tamanho: 9 },
  ],
}

const PALETA_CORES = [
  { bg: 'rgba(56, 189, 248, 0.28)', border: '#38bdf8', text: '#38bdf8' }, // Azul ciano
  { bg: 'rgba(34, 197, 94, 0.28)', border: '#22c55e', text: '#4ade80' },  // Verde
  { bg: 'rgba(236, 72, 153, 0.28)', border: '#ec4899', text: '#f472b6' }, // Rosa
  { bg: 'rgba(245, 158, 11, 0.28)', border: '#f59e0b', text: '#fbbf24' }, // Âmbar
  { bg: 'rgba(168, 85, 247, 0.28)', border: '#a855f7', text: '#c084fc' }, // Roxo
  { bg: 'rgba(239, 68, 68, 0.28)', border: '#ef4444', text: '#f87171' },  // Vermelho
  { bg: 'rgba(14, 165, 233, 0.28)', border: '#0ea5e9', text: '#7dd3fc' }, // Azul royal
  { bg: 'rgba(132, 204, 22, 0.28)', border: '#84cc16', text: '#a3e635' }, // Lima
  { bg: 'rgba(249, 115, 22, 0.28)', border: '#f97316', text: '#fb923c' }, // Laranja
  { bg: 'rgba(99, 102, 241, 0.28)', border: '#6366f1', text: '#818cf8' }, // Índigo
]

// Modal centralizado
const CenterModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(3, 7, 18, 0.85);
  backdrop-filter: blur(10px);
  padding: 20px;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const CenterModalDialog = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1120px;
  max-height: 92vh;
  background: #0b1120;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.8);
  overflow: hidden;
  animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes zoomIn {
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: scale(1); }
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.02);

    .btn-close {
      background: transparent;
      border: none;
      color: #94a3b8;
      font-size: 26px;
      cursor: pointer;
      display: flex;
      align-items: center;
      transition: color 0.15s;

      &:hover {
        color: #f87171;
      }
    }
  }

  .body {
    padding: 20px 24px;
    overflow-y: auto;
    max-height: calc(92vh - 75px);
  }
`

const TabTipoContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

const TabTipoButton = styled.button<{ $active: boolean; $locked?: boolean }>`
  background: ${(props) =>
    props.$active
      ? 'rgba(56, 189, 248, 0.15)'
      : props.$locked
      ? 'rgba(255, 255, 255, 0.01)'
      : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${(props) =>
    props.$active
      ? '#38bdf8'
      : props.$locked
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(255, 255, 255, 0.08)'};
  color: ${(props) =>
    props.$active
      ? '#38bdf8'
      : props.$locked
      ? '#64748b'
      : '#94a3b8'};
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: ${(props) => (props.$locked ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$locked ? 0.6 : 1)};
  white-space: nowrap;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    ${(props) =>
      props.$locked
        ? 'background: rgba(239, 68, 68, 0.08); color: #f87171; border-color: rgba(239, 68, 68, 0.25);'
        : 'background: rgba(56, 189, 248, 0.2); color: #ffffff;'}
  }
`

const DropzoneWrapper = styled.div<{ $isDragging?: boolean }>`
  border: 2px dashed ${(props) => (props.$isDragging ? '#38bdf8' : 'rgba(255, 255, 255, 0.15)')};
  background: ${(props) => (props.$isDragging ? 'rgba(56, 189, 248, 0.08)' : 'rgba(15, 23, 42, 0.5)')};
  border-radius: 12px;
  padding: 16px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: #38bdf8;
    background: rgba(56, 189, 248, 0.05);
  }

  .icon {
    font-size: 28px;
    color: #38bdf8;
    margin-bottom: 4px;
  }

  h4 {
    margin: 0 0 2px 0;
    font-size: 13px;
    color: #ffffff;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 11px;
    color: #94a3b8;
  }
`

const StatusAlert = styled.div<{ $isSuccess: boolean }>`
  background: ${(props) =>
    props.$isSuccess
      ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(21, 128, 61, 0.15))'
      : 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(180, 83, 9, 0.15))'};
  border: 1px solid ${(props) => (props.$isSuccess ? 'rgba(34, 197, 94, 0.4)' : 'rgba(245, 158, 11, 0.4)')};
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;

  .alert-icon {
    font-size: 20px;
    color: ${(props) => (props.$isSuccess ? '#4ade80' : '#fbbf24')};
    flex-shrink: 0;
    margin-top: 1px;
  }

  .alert-content {
    font-size: 12px;
    color: #e2e8f0;
    line-height: 1.4;

    strong {
      color: #ffffff;
      display: block;
      font-size: 12px;
      margin-bottom: 2px;
    }

    ul {
      margin: 4px 0 0 0;
      padding-left: 16px;
      color: #fca5a5;
    }
  }
`

// Contêiner principal do visualizador
const ViewerContainer = styled.div`
  background: #050811;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  user-select: none;
`

const TextTrackScroll = styled.div`
  overflow-x: auto;
  padding-bottom: 10px;
  position: relative;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

// Envoltório para sincronizar a rolagem horizontal de régua, setinhas e texto
const TrackContentWrapper = styled.div<{ $widthPx: number }>`
  min-width: ${(props) => props.$widthPx}px;
  display: inline-block;
  position: relative;
`

// Régua vetorial de alta precisão com traços em cima e números embaixo
interface SvgRulerProps {
  totalChars: number
  charWidth: number
}

const SvgRuler: React.FC<SvgRulerProps> = React.memo(({ totalChars, charWidth }) => {
  const width = Math.max(totalChars * charWidth, 400)
  const ticks: React.ReactNode[] = []

  for (let i = 0; i <= totalChars; i++) {
    const x = i * charWidth
    const isMajor = i % 10 === 0
    const isMedium = i % 5 === 0 && !isMajor

    if (isMajor) {
      ticks.push(
        <g key={i}>
          {/* Traço em cima onde tem a régua */}
          <line x1={x} y1={0} x2={x} y2={8} stroke="#94a3b8" strokeWidth={1.5} />
          {/* Número embaixo do traço */}
          <text
            x={x + 2}
            y={20}
            fill="#94a3b8"
            fontSize="10"
            fontFamily="'Fira Code', 'Courier New', Courier, monospace"
            fontWeight="600"
          >
            {i}
          </text>
        </g>,
      )
    } else if (isMedium) {
      ticks.push(
        <line key={i} x1={x} y1={0} x2={x} y2={5} stroke="#64748b" strokeWidth={1.2} />,
      )
    } else {
      ticks.push(
        <line key={i} x1={x} y1={0} x2={x} y2={3} stroke="#334155" strokeWidth={1} />,
      )
    }
  }

  return (
    <svg
      width={width}
      height={24}
      style={{ display: 'block', overflow: 'visible', marginBottom: '6px' }}
    >
      {/* Linha horizontal superior contínua da régua */}
      <line x1={0} y1={0} x2={width} y2={0} stroke="rgba(255, 255, 255, 0.2)" strokeWidth={1} />
      {ticks}
    </svg>
  )
})

// Faixa superior onde ficam as setinhas e barras de controle
const TopMarkerTrack = styled.div<{ $widthPx: number }>`
  position: relative;
  height: 28px;
  min-width: ${(props) => props.$widthPx}px;
  margin-bottom: 3px;
`

const TopFieldMarker = styled.div<{
  $leftPx: number
  $widthPx: number
  $color: string
  $isActive: boolean
}>`
  position: absolute;
  left: ${(props) => props.$leftPx}px;
  width: ${(props) => props.$widthPx}px;
  top: 0;
  height: 26px;
  z-index: ${(props) => (props.$isActive ? 25 : 10)};
  cursor: default;

  .top-bar-connector {
    position: absolute;
    left: 0;
    right: 0;
    top: 5px;
    height: 4px;
    background: ${(props) => props.$color};
    border-radius: 2px;
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;

    &:hover {
      filter: brightness(1.25);
      box-shadow: 0 0 8px ${(props) => props.$color};
    }
  }

  .field-pill-label {
    position: absolute;
    top: -15px;
    font-size: 10px;
    font-weight: 700;
    color: #ffffff;
    background: ${(props) => props.$color};
    padding: 1px 6px;
    border-radius: 4px;
    white-space: nowrap;
    pointer-events: none;
    display: ${(props) => (props.$isActive ? 'block' : 'none')};
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
  }

  &:hover .field-pill-label {
    display: block;
  }
`

// Setinhas superiores para selecionar início e fim com alças de arrasto precisas
const ArrowPinHandle = styled.div<{
  $color: string
  $isStart?: boolean
  $isActive: boolean
}>`
  position: absolute;
  top: 0;
  ${(props) => (props.$isStart ? 'left: 0;' : 'right: 0;')}
  transform: translateX(${(props) => (props.$isStart ? '-50%' : '50%')});
  width: 18px;
  height: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  cursor: col-resize;
  z-index: ${(props) => (props.$isActive ? 35 : 20)};
  transition: transform 0.1s ease, filter 0.15s ease;

  &:hover {
    transform: translateX(${(props) => (props.$isStart ? '-50%' : '50%')}) scale(1.2);
    filter: drop-shadow(0 0 8px ${(props) => props.$color});
    z-index: 40;
  }

  .pin-head {
    width: 16px;
    height: 14px;
    background: ${(props) => props.$color};
    border-radius: 3px 3px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #050811;
    font-size: 10px;
    font-weight: 900;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  }

  .pin-point {
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 7px solid ${(props) => props.$color};
  }
`

// Linha de dados real (texto monospace puro e nítido)
const TextDataRow = styled.div<{ $widthPx: number }>`
  font-family: 'Fira Code', 'Courier New', Courier, monospace;
  font-size: 14px;
  line-height: 36px;
  white-space: pre;
  position: relative;
  height: 38px;
  min-width: ${(props) => props.$widthPx}px;
  color: #f1f5f9;
  letter-spacing: 0;
`

// Moldura bem fina ao redor dos números selecionados (não obstrui nem polui os dados)
const ThinHighlightBox = styled.div<{
  $leftPx: number
  $widthPx: number
  $color: string
  $isActive: boolean
}>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: ${(props) => props.$leftPx}px;
  width: ${(props) => props.$widthPx}px;
  background: ${(props) => (props.$isActive ? `${props.$color}24` : `${props.$color}0d`)};
  border: 1px solid ${(props) => (props.$isActive ? props.$color : `${props.$color}80`)};
  border-radius: 4px;
  box-sizing: border-box;
  z-index: ${(props) => (props.$isActive ? 10 : 4)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(props) => `${props.$color}30`};
    border-color: ${(props) => props.$color};
    box-shadow: 0 0 8px ${(props) => `${props.$color}40`};
  }
`

const FieldCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
  max-height: 250px;
  overflow-y: auto;
  padding-right: 4px;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

const FieldCard = styled.div<{ $color: string; $active: boolean }>`
  background: ${(props) => (props.$active ? 'rgba(56, 189, 248, 0.1)' : 'rgba(15, 23, 42, 0.6)')};
  border: 1px solid ${(props) => (props.$active ? props.$color : 'rgba(255, 255, 255, 0.08)')};
  border-left: 4px solid ${(props) => props.$color};
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.15s;
  cursor: pointer;

  &:hover {
    border-color: ${(props) => props.$color};
    background: rgba(15, 23, 42, 0.85);
  }

  .card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .field-name {
      font-size: 12px;
      font-weight: 700;
      color: #ffffff;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .card-actions {
      display: flex;
      align-items: center;
      gap: 6px;

      .range-tag {
        font-size: 11px;
        font-weight: 700;
        background: rgba(255, 255, 255, 0.06);
        padding: 2px 8px;
        border-radius: 4px;
        color: ${(props) => props.$color};
      }

      .btn-del {
        background: transparent;
        border: none;
        color: #94a3b8;
        font-size: 14px;
        cursor: pointer;
        padding: 0;
        display: flex;
        align-items: center;

        &:hover {
          color: #f87171;
        }
      }
    }
  }

  .card-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .ctrl-group {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      color: #94a3b8;

      .mini-btn {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.12);
        color: #cbd5e1;
        border-radius: 4px;
        width: 20px;
        height: 22px;
        font-size: 11px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: ${(props) => props.$color};
          color: #0f172a;
        }
      }

      input {
        width: 44px;
        height: 24px;
        background: rgba(0, 0, 0, 0.4);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 4px;
        color: #ffffff;
        font-size: 11px;
        text-align: center;

        &:focus {
          outline: none;
          border-color: ${(props) => props.$color};
        }
      }
    }
  }

  .card-preview {
    font-size: 11px;
    background: rgba(0, 0, 0, 0.35);
    padding: 4px 8px;
    border-radius: 4px;
    color: #cbd5e1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    span {
      color: ${(props) => props.$color};
      font-weight: 700;
    }
  }
`

const AddMarkerBox = styled.div`
  background: rgba(56, 189, 248, 0.05);
  border: 1px dashed rgba(56, 189, 248, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  input {
    height: 32px;
    background: #0f172a;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    color: #ffffff;
    font-size: 12px;
    padding: 0 10px;

    &:focus {
      outline: none;
      border-color: #38bdf8;
    }
  }

  button.btn-add-action {
    background: #0284c7;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    height: 32px;
    padding: 0 14px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
      background: #0369a1;
    }
  }
`

export function ModalLayoutAssistant({
  isOpen,
  onClose,
  tipoInicial = 'vendas',
  distId,
  layoutsCadastrados,
  onApplyLayout,
}: Props) {
  const [tipoArquivo, setTipoArquivo] = useState<string>(tipoInicial)
  const [cachedLayouts, setCachedLayouts] = useState<Record<string, any[]>>(layoutsCadastrados || {})
  const [nomeArquivo, setNomeArquivo] = useState<string>('')
  const [conteudoTxt, setConteudoTxt] = useState<string>('')
  const [linhaOriginal, setLinhaOriginal] = useState<string>('')
  const [linhaDados, setLinhaDados] = useState<string>('')
  const [prefixo, setPrefixo] = useState<string>('')
  const [temPrefixo, setTemPrefixo] = useState<boolean>(true)
  const [campos, setCampos] = useState<any[]>([])
  const [erros, setErros] = useState<string[]>([])
  const [isValido, setIsValido] = useState<boolean>(false)
  const [activeFieldIndex, setActiveFieldIndex] = useState<number | null>(null)
  const [isDraggingFile, setIsDraggingFile] = useState<boolean>(false)
  const [showAddMarker, setShowAddMarker] = useState<boolean>(false)

  // Estado para novo marcador
  const [newFieldName, setNewFieldName] = useState<string>('')
  const [newFieldStart, setNewFieldStart] = useState<number>(0)
  const [newFieldLen, setNewFieldLen] = useState<number>(10)

  // Medição da largura de 1 caractere na fonte monospace
  const [charWidth, setCharWidth] = useState<number>(8.4)
  const charMeasurerRef = useRef<HTMLSpanElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setTipoArquivo(tipoInicial)
  }, [tipoInicial])

  useEffect(() => {
    if (layoutsCadastrados) {
      setCachedLayouts((prev) => ({ ...prev, ...layoutsCadastrados }))
    }
  }, [layoutsCadastrados])

  // Se distId foi fornecido, busca o layout cadastrado no banco
  useEffect(() => {
    if (isOpen && distId) {
      api
        .get(`/layout/posicao/${distId}`)
        .then((res) => {
          const fetched = res.data?.layout?.layouts || {}
          setCachedLayouts((prev) => ({ ...fetched, ...prev }))
        })
        .catch((err) => {
          console.warn('Layout existente não encontrado para o distribuidor:', err)
        })
    }
  }, [isOpen, distId])

  // Inicializa ou atualiza os campos da aba ativa (seja cadastrado ou padrão)
  const carregarCamposDaAba = useCallback(
    (tipo: string, mapaLayouts?: Record<string, any[]>) => {
      const layouts = mapaLayouts || cachedLayouts
      const cadastrados = layouts?.[tipo]

      // 1. Se existem campos cadastrados para este tipo, carrega-os com prioridade
      if (Array.isArray(cadastrados) && cadastrados.length > 0) {
        const formatados = cadastrados.map((c: any, idx: number) => {
          const inicio = Number(
            c.inicio !== undefined ? c.inicio : c.posicao_ini !== undefined ? c.posicao_ini : 0,
          )
          const tamanho = Number(c.tamanho || 10)
          return {
            campo: c.select || c.campo || c.coluna || `campo_${idx}`,
            label: c.label || c.texto_coluna || c.campo || `Campo ${idx + 1}`,
            inicio,
            tamanho,
            fim: inicio + tamanho,
            valorBruto: '',
            valor: '',
            corObj: PALETA_CORES[idx % PALETA_CORES.length],
          }
        })
        setCampos(formatados)
        if (formatados.length > 0) setActiveFieldIndex(0)
        return
      }

      // 2. Caso contrário, carrega o layout padrão do sistema para aquela aba
      const padrao = LAYOUTS_PADRAO[tipo] || LAYOUTS_PADRAO.vendas
      const camposPadrao = padrao.map((c, idx) => ({
        campo: c.campo,
        label: c.label,
        inicio: c.inicio,
        tamanho: c.tamanho,
        fim: c.inicio + c.tamanho,
        valorBruto: '',
        valor: '',
        corObj: PALETA_CORES[idx % PALETA_CORES.length],
      }))
      setCampos(camposPadrao)
      if (camposPadrao.length > 0) setActiveFieldIndex(0)
    },
    [cachedLayouts],
  )

  // Sempre que abre o modal ou muda de aba (sem arquivo carregado), garante que os cards estão exibidos
  useEffect(() => {
    if (isOpen && !conteudoTxt) {
      carregarCamposDaAba(tipoArquivo)
    }
  }, [isOpen, tipoArquivo, carregarCamposDaAba, conteudoTxt])

  // Mede com alta precisão a largura de 1 caractere em pixels
  useEffect(() => {
    if (charMeasurerRef.current) {
      const rect = charMeasurerRef.current.getBoundingClientRect()
      if (rect.width > 0) {
        setCharWidth(rect.width / 50)
      }
    }
  }, [linhaDados])

  // Analisa o arquivo TXT na API, preservando o layout atualmente cadastrado na tela
  const handleAnalyzeTxt = async (txt: string, fileName?: string, tipoForcado?: string) => {
    if (!txt.trim()) return
    try {
      const tipoAlvo = tipoForcado || tipoArquivo

      // Envia os campos atualmente na tela como camposCustomizados para a API respeitar o layout cadastrado
      const res = await api.post('/layout/analisar', {
        conteudoTxt: txt,
        nomeArquivo: fileName || nomeArquivo,
        tipo: tipoAlvo,
        camposCustomizados:
          campos.length > 0
            ? campos.map((c) => ({
                campo: c.campo,
                label: c.label,
                inicio: c.inicio,
                tamanho: c.tamanho,
              }))
            : undefined,
      })

      const data = res.data
      setTipoArquivo(data.tipo)
      setPrefixo(data.prefixo)
      setTemPrefixo(data.temPrefixo)
      setLinhaOriginal(data.linhaOriginal)
      setLinhaDados(data.linhaDados)
      setIsValido(data.valido)
      setErros(data.erros || [])

      const camposComCor = (data.campos || []).map((c: any, idx: number) => ({
        ...c,
        corObj: PALETA_CORES[idx % PALETA_CORES.length],
      }))
      setCampos(camposComCor)
      if (camposComCor.length > 0) setActiveFieldIndex(0)

      toast.success(`Layout ${data.tipo.toUpperCase()} analisado seguindo o seu mapeamento!`)
    } catch (err: any) {
      console.error('Erro ao analisar TXT:', err)
      toast.error(err.response?.data?.error || 'Erro ao analisar arquivo TXT.')
    }
  }

  // Leitura de arquivo
  const handleFile = (file: File) => {
    setNomeArquivo(file.name)
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      setConteudoTxt(content)
      handleAnalyzeTxt(content, file.name)
    }
    reader.readAsText(file, 'ISO-8859-1')
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDraggingFile(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  // Limpar arquivo ativo para trocar de layout ou selecionar outra aba com segurança
  const handleResetFile = () => {
    setNomeArquivo('')
    setConteudoTxt('')
    setLinhaOriginal('')
    setLinhaDados('')
    setErros([])
    setIsValido(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    // Restaura os cards cadastrados ou padrão para que permaneçam na tela
    carregarCamposDaAba(tipoArquivo)
    toast.info('Arquivo removido. Os cards de layout permanecem disponíveis para ajuste.')
  }

  // Trata clique nas abas protegendo contra mistura indevida de layouts (ex: vendas em estoque)
  const handleTabClick = (tabId: string, tabLabel: string) => {
    if (tabId === tipoArquivo) return

    // Se já existe um arquivo carregado, bloqueia a troca e orienta via toast (sem alerts nativos)
    if (linhaDados && conteudoTxt) {
      toast.warning(
        `O arquivo carregado é de ${tipoArquivo.toUpperCase()}. Para configurar ${tabLabel}, clique primeiro em 'Limpar / Trocar Arquivo'.`
      )
      return
    }

    // Se não há arquivo carregado, troca livremente
    setTipoArquivo(tabId)
  }

  // Atualização numérica de início e tamanho
  const handleUpdateField = (index: number, field: 'inicio' | 'tamanho', value: number) => {
    const novosCampos = [...campos]
    const target = { ...novosCampos[index] }
    target[field] = Math.max(0, value)
    target.fim = target.inicio + target.tamanho

    if (linhaDados) {
      target.valorBruto = linhaDados.substring(target.inicio, target.fim)
      target.valor = target.valorBruto.trim()
    }
    novosCampos[index] = target
    setCampos(novosCampos)
  }

  // Mover início ou tamanho com passos
  const handleShiftField = (index: number, delta: number) => {
    const novosCampos = [...campos]
    const target = { ...novosCampos[index] }
    target.inicio = Math.max(0, target.inicio + delta)
    target.fim = target.inicio + target.tamanho

    if (linhaDados) {
      target.valorBruto = linhaDados.substring(target.inicio, target.fim)
      target.valor = target.valorBruto.trim()
    }
    novosCampos[index] = target
    setCampos(novosCampos)
  }

  const handleResizeField = (index: number, delta: number) => {
    const novosCampos = [...campos]
    const target = { ...novosCampos[index] }
    target.tamanho = Math.max(1, target.tamanho + delta)
    target.fim = target.inicio + target.tamanho

    if (linhaDados) {
      target.valorBruto = linhaDados.substring(target.inicio, target.fim)
      target.valor = target.valorBruto.trim()
    }
    novosCampos[index] = target
    setCampos(novosCampos)
  }

  // ARRASTO INTERATIVO DAS BARRAS DE SELEÇÃO:
  // Modo: 'move' (arrasta barra inteira), 'resize-left' (muda inicio), 'resize-right' (cresce tamanho)
  const startDrag = useCallback(
    (e: React.MouseEvent, index: number, mode: 'move' | 'resize-left' | 'resize-right') => {
      e.preventDefault()
      e.stopPropagation()
      setActiveFieldIndex(index)

      const startX = e.clientX
      const currentField = campos[index]
      const origInicio = currentField.inicio
      const origTamanho = currentField.tamanho
      const origFim = origInicio + origTamanho

      const onMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX
        const deltaChars = Math.round(deltaX / charWidth)

        setCampos((prevCampos) => {
          const list = [...prevCampos]
          const f = { ...list[index] }

          if (mode === 'resize-right') {
            // Crescer ou diminuir tamanho
            f.tamanho = Math.max(1, origTamanho + deltaChars)
            f.fim = f.inicio + f.tamanho
          } else if (mode === 'resize-left') {
            // Mudar início (não ultrapassando o fim)
            const novoInicio = Math.max(0, Math.min(origInicio + deltaChars, origFim - 1))
            f.inicio = novoInicio
            f.tamanho = origFim - novoInicio
            f.fim = origFim
          } else if (mode === 'move') {
            // Mover a barra inteira mantendo o tamanho
            f.inicio = Math.max(0, origInicio + deltaChars)
            f.fim = f.inicio + f.tamanho
          }

          if (linhaDados) {
            f.valorBruto = linhaDados.substring(f.inicio, f.fim)
            f.valor = f.valorBruto.trim()
          }

          list[index] = f
          return list
        })
      }

      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('mouseup', onMouseUp)
      }

      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    },
    [campos, charWidth, linhaDados],
  )

  // Adicionar novo marcador
  const handleAddNewMarker = () => {
    if (!newFieldName.trim()) {
      toast.error('Informe o nome do novo campo/marcador.')
      return
    }

    const novoCampo = {
      campo: newFieldName.toLowerCase().replace(/\s+/g, '_'),
      label: newFieldName.trim(),
      inicio: Number(newFieldStart) || 0,
      tamanho: Number(newFieldLen) || 10,
      fim: (Number(newFieldStart) || 0) + (Number(newFieldLen) || 10),
      valorBruto: linhaDados
        ? linhaDados.substring(newFieldStart, newFieldStart + newFieldLen)
        : '',
      valor: linhaDados
        ? linhaDados.substring(newFieldStart, newFieldStart + newFieldLen).trim()
        : '',
      corObj: PALETA_CORES[campos.length % PALETA_CORES.length],
    }

    setCampos([...campos, novoCampo])
    setActiveFieldIndex(campos.length)
    setNewFieldName('')
    setShowAddMarker(false)
    toast.success(`Marcador '${novoCampo.label}' adicionado!`)
  }

  // Remover campo
  const handleDeleteField = (index: number) => {
    const nome = campos[index].label
    const filtrados = campos.filter((_, idx) => idx !== index)
    setCampos(filtrados)
    if (activeFieldIndex === index) {
      setActiveFieldIndex(filtrados.length > 0 ? 0 : null)
    }
    toast.info(`Marcador '${nome}' removido.`)
  }

  // Largura e total de caracteres da régua e trilhas
  const totalChars = useMemo(() => {
    return Math.max(linhaDados ? linhaDados.length : 140, 140)
  }, [linhaDados])

  const trackWidth = useMemo(() => {
    return totalChars * charWidth
  }, [totalChars, charWidth])

  // Aplicar layout no formulário pai ou salvar no banco
  const handleConfirmApply = async () => {
    if (campos.length === 0) {
      toast.error('Nenhum campo mapeado para aplicar.')
      return
    }

    const camposMapeados = campos.map((c) => ({
      select: c.campo,
      campo: c.campo,
      inicio: c.inicio,
      tamanho: c.tamanho,
      label: c.label,
    }))

    // Se temos distId, persiste imediatamente na tabela do banco
    if (distId) {
      try {
        await api.post(`/layout/posicao/${distId}`, {
          grupo: tipoArquivo,
          campos: camposMapeados,
        })
        setCachedLayouts((prev) => ({ ...prev, [tipoArquivo]: camposMapeados }))
      } catch (err) {
        console.error('Erro ao salvar layout no banco:', err)
      }
    }

    onApplyLayout(tipoArquivo, camposMapeados)
    toast.success(`Mapeamento de ${tipoArquivo.toUpperCase()} aplicado com sucesso!`)
    onClose()
  }

  if (!isOpen) return null

  return (
    <CenterModalBackdrop>
      <CenterModalDialog>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IoColorWandOutline style={{ fontSize: '26px', color: '#38bdf8' }} />
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#ffffff', fontWeight: 800 }}>
                Assistente & Editor de Layout TXT
              </h3>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                Arraste as barras indicadoras diretamente sobre os dados para selecionar os campos
              </span>
            </div>
          </div>
          <button className="btn-close" onClick={onClose} type="button">
            <IoCloseCircleSharp />
          </button>
        </div>

        <div className="body">
          {/* Span invisível para medir a largura exata de 1 caractere monospace */}
          <span
            ref={charMeasurerRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              fontFamily: "'Fira Code', 'Courier New', Courier, monospace",
              fontSize: '14px',
              whiteSpace: 'pre',
            }}
          >
            {'01234567890123456789012345678901234567890123456789'}
          </span>

          {/* Abas por tipo de arquivo com proteção de compatibilidade */}
          <TabTipoContainer>
            {[
              { id: 'vendas', label: 'Vendas / Sellout', icon: <FiDollarSign /> },
              { id: 'estoque', label: 'Estoque', icon: <FiPackage /> },
              { id: 'produtos', label: 'Produtos', icon: <FiTag /> },
              { id: 'forcavenda', label: 'Força de Vendas', icon: <FiUser /> },
              { id: 'clientes', label: 'Clientes', icon: <FiHash /> },
            ].map((tab) => {
              const hasFile = Boolean(linhaDados && conteudoTxt)
              const isLocked = hasFile && tipoArquivo !== tab.id
              const isActive = tipoArquivo === tab.id

              return (
                <TabTipoButton
                  key={tab.id}
                  type="button"
                  $active={isActive}
                  $locked={isLocked}
                  title={
                    isLocked
                      ? `Arquivo de ${tipoArquivo.toUpperCase()} carregado. Limpe ou troque o arquivo para selecionar ${tab.label}.`
                      : tab.label
                  }
                  onClick={() => handleTabClick(tab.id, tab.label)}
                >
                  {isLocked ? <FiLock style={{ fontSize: '13px', color: '#94a3b8' }} /> : tab.icon}
                  {tab.label}
                </TabTipoButton>
              )
            })}
          </TabTipoContainer>

          {/* Área de Dropzone com botão para trocar/limpar arquivo */}
          <DropzoneWrapper
            $isDragging={isDraggingFile}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDraggingFile(true)
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFile(e.target.files[0])
                }
              }}
            />
            <IoCloudUploadOutline className="icon" />
            <h4>
              {nomeArquivo
                ? `Arquivo ativo: ${nomeArquivo}`
                : `Arraste o arquivo TXT de ${tipoArquivo.toUpperCase()} aqui (ou clique para selecionar)`}
            </h4>
            <p>
              {nomeArquivo
                ? `Mapeando layout de ${tipoArquivo.toUpperCase()} • O cabeçalho foi ignorado automaticamente.`
                : 'O cabeçalho é ignorado automaticamente e os dados reais são carregados com as barras.'}
            </p>

            {nomeArquivo && (
              <div style={{ marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleResetFile()
                  }}
                  style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.35)',
                    color: '#f87171',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '5px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.15s ease',
                  }}
                  title="Remover arquivo ativo para importar outro ou trocar de aba"
                >
                  <IoTrashOutline style={{ fontSize: '14px' }} />
                  Limpar / Trocar Arquivo
                </button>
              </div>
            )}
          </DropzoneWrapper>

          {/* Diagnóstico do Layout */}
          {linhaDados && (
            <StatusAlert $isSuccess={isValido}>
              {isValido ? <IoCheckmarkCircle className="alert-icon" /> : <IoAlertCircle className="alert-icon" />}
              <div className="alert-content">
                <strong>
                  {isValido ? 'Layout 100% Compatível com o Padrão GID!' : 'Divergências Detectadas:'}
                </strong>
                {isValido ? (
                  <span>Todos os campos foram identificados e posicionados de acordo com o padrão do sistema.</span>
                ) : (
                  <>
                    <span>O arquivo recebido possui posições ou tamanhos diferentes. Arraste as barras indicadoras para os locais corretos:</span>
                    <ul>
                      {erros.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </StatusAlert>
          )}

          {/* VISUALIZADOR COM RÉGUA SUPERIOR, SETINHAS E MOLDURAS FINAS */}
          {linhaDados && (
            <ViewerContainer>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8' }}>
                  Linha de Dados Completa ({linhaDados.length} caracteres) • Posição 0: Registro <strong style={{ color: '#f472b6' }}>'{prefixo || linhaDados.charAt(0)}'</strong>
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                  CNPJ e dados iniciam na posição <strong style={{ color: '#38bdf8' }}>1</strong> • Marcadores ajustáveis com as setinhas
                </span>
              </div>

              <TextTrackScroll>
                <TrackContentWrapper $widthPx={trackWidth}>
                  {/* 1. Régua com traços em cima e números embaixo */}
                  <SvgRuler totalChars={totalChars} charWidth={charWidth} />

                  {/* 2. Faixa superior dos marcadores com as setinhas para selecionar onde começa e onde termina */}
                  <TopMarkerTrack $widthPx={trackWidth}>
                    {campos.map((c, idx) => {
                      const isActive = activeFieldIndex === idx
                      const leftPx = c.inicio * charWidth
                      const widthPx = Math.max(c.tamanho * charWidth, 16)

                      return (
                        <TopFieldMarker
                          key={c.campo}
                          $leftPx={leftPx}
                          $widthPx={widthPx}
                          $color={c.corObj.border}
                          $isActive={isActive}
                          onClick={() => setActiveFieldIndex(idx)}
                        >
                          {/* Barra superior conectora que permite arrastar o campo inteiro */}
                          <div
                            className="top-bar-connector"
                            onMouseDown={(e) => startDrag(e, idx, 'move')}
                            title={`${c.label} [${c.inicio} a ${c.fim}] • Arraste para mover o campo`}
                          >
                            <span className="field-pill-label">
                              {c.label} ({c.inicio} a {c.fim})
                            </span>
                          </div>

                          {/* Setinha esquerda para selecionar onde começa */}
                          <ArrowPinHandle
                            $color={c.corObj.border}
                            $isStart={true}
                            $isActive={isActive}
                            title={`Início: ${c.inicio} • Arraste a setinha para alterar onde começa`}
                            onMouseDown={(e) => startDrag(e, idx, 'resize-left')}
                          >
                            <div className="pin-head">
                              <FiChevronLeft style={{ strokeWidth: 3 }} />
                            </div>
                            <div className="pin-point" />
                          </ArrowPinHandle>

                          {/* Setinha direita para selecionar onde termina */}
                          <ArrowPinHandle
                            $color={c.corObj.border}
                            $isStart={false}
                            $isActive={isActive}
                            title={`Fim: ${c.fim} (${c.tamanho} chars) • Arraste a setinha para alterar onde termina`}
                            onMouseDown={(e) => startDrag(e, idx, 'resize-right')}
                          >
                            <div className="pin-head">
                              <FiChevronRight style={{ strokeWidth: 3 }} />
                            </div>
                            <div className="pin-point" />
                          </ArrowPinHandle>
                        </TopFieldMarker>
                      )
                    })}
                  </TopMarkerTrack>

                  {/* 3. Linha de dados com molduras BEM FINAS ao redor dos números */}
                  <TextDataRow $widthPx={trackWidth}>
                    {/* Texto real dos dados */}
                    {linhaDados}

                    {/* Destaque sutil do caractere de tipo de registro (posição 0) */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '3px',
                        bottom: '3px',
                        left: '0px',
                        width: `${Math.max(charWidth, 8)}px`,
                        background: 'rgba(244, 114, 182, 0.18)',
                        border: '1px dashed #f472b6',
                        borderRadius: '3px',
                        pointerEvents: 'none',
                        zIndex: 5,
                      }}
                      title="Posição 0: Identificador do Tipo de Registro (ex: 'D', 'E', 'V')"
                    />

                    {/* Destaques finos ao redor de cada campo */}
                    {campos.map((c, idx) => {
                      const isActive = activeFieldIndex === idx
                      const leftPx = c.inicio * charWidth
                      const widthPx = Math.max(c.tamanho * charWidth, 4)

                      return (
                        <ThinHighlightBox
                          key={c.campo}
                          $leftPx={leftPx}
                          $widthPx={widthPx}
                          $color={c.corObj.border}
                          $isActive={isActive}
                          onClick={() => setActiveFieldIndex(idx)}
                          onMouseDown={(e) => startDrag(e, idx, 'move')}
                          title={`${c.label} [${c.inicio} a ${c.fim}] • Clique para selecionar`}
                        />
                      )
                    })}
                  </TextDataRow>
                </TrackContentWrapper>
              </TextTrackScroll>
            </ViewerContainer>
          )}

          {/* Botão Dedicado: Adicionar Novo Marcador */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FiSliders /> Campos e Marcadores Configurados ({campos.length})
            </span>

            <button
              type="button"
              onClick={() => {
                const last = campos[campos.length - 1]
                const nextStart = last ? last.inicio + last.tamanho : 0
                setNewFieldStart(nextStart)
                setNewFieldLen(10)
                setShowAddMarker(!showAddMarker)
              }}
              style={{
                background: 'rgba(56, 189, 248, 0.15)',
                border: '1px solid #38bdf8',
                color: '#38bdf8',
                fontSize: '12px',
                fontWeight: 700,
                padding: '6px 14px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s',
              }}
            >
              <FiPlus />
              <span>Adicionar Novo Marcador</span>
            </button>
          </div>

          {/* Formulário para novo marcador */}
          {showAddMarker && (
            <AddMarkerBox>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8' }}>
                Novo Marcador:
              </span>
              <input
                type="text"
                placeholder="Nome do Campo (ex: Filial, Desconto)"
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                style={{ flex: 1, minWidth: '180px' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#94a3b8' }}>
                <span>Início:</span>
                <input
                  type="number"
                  value={newFieldStart}
                  onChange={(e) => setNewFieldStart(Number(e.target.value))}
                  style={{ width: '56px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#94a3b8' }}>
                <span>Tamanho:</span>
                <input
                  type="number"
                  value={newFieldLen}
                  onChange={(e) => setNewFieldLen(Number(e.target.value))}
                  style={{ width: '56px' }}
                />
              </div>
              <button type="button" className="btn-add-action" onClick={handleAddNewMarker}>
                <IoAddCircleOutline style={{ fontSize: '16px' }} />
                <span>Criar</span>
              </button>
              <button
                type="button"
                onClick={() => setShowAddMarker(false)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '12px', cursor: 'pointer' }}
              >
                Cancelar
              </button>
            </AddMarkerBox>
          )}

          {/* Cards dos Campos com Informações, Ajustes e Lixeira */}
          {campos.length > 0 && (
            <FieldCardGrid>
              {campos.map((c, idx) => (
                <FieldCard
                  key={c.campo}
                  $color={c.corObj.border}
                  $active={activeFieldIndex === idx}
                  onClick={() => setActiveFieldIndex(idx)}
                >
                  <div className="card-top">
                    <span className="field-name">
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: c.corObj.border,
                          display: 'inline-block',
                        }}
                      />
                      {c.label}
                    </span>
                    <div className="card-actions">
                      <span className="range-tag">
                        [{c.inicio} a {c.inicio + c.tamanho}] ({c.tamanho} chars)
                      </span>
                      <button
                        type="button"
                        className="btn-del"
                        title="Excluir este marcador"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteField(idx)
                        }}
                      >
                        <IoTrashOutline />
                      </button>
                    </div>
                  </div>

                  <div className="card-controls">
                    <div className="ctrl-group">
                      <span>Início:</span>
                      <button
                        type="button"
                        className="mini-btn"
                        title="Mover -1"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleShiftField(idx, -1)
                        }}
                      >
                        <FiChevronLeft />
                      </button>
                      <input
                        type="number"
                        value={c.inicio}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleUpdateField(idx, 'inicio', Number(e.target.value))}
                      />
                      <button
                        type="button"
                        className="mini-btn"
                        title="Mover +1"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleShiftField(idx, 1)
                        }}
                      >
                        <FiChevronRight />
                      </button>
                    </div>

                    <div className="ctrl-group">
                      <span>Tamanho:</span>
                      <button
                        type="button"
                        className="mini-btn"
                        title="Diminuir -1"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleResizeField(idx, -1)
                        }}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        value={c.tamanho}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleUpdateField(idx, 'tamanho', Number(e.target.value))}
                      />
                      <button
                        type="button"
                        className="mini-btn"
                        title="Aumentar +1"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleResizeField(idx, 1)
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div
                    className="card-preview"
                    title={c.valorBruto || `Posição: [${c.inicio} a ${c.fim}] (${c.tamanho} chars)`}
                  >
                    <span>{linhaDados ? 'Prévia:' : 'Posição:'}</span>{' '}
                    {linhaDados
                      ? c.valor
                        ? `"${c.valor}"`
                        : '<vazio>'
                      : `[${c.inicio} a ${c.fim}] (${c.tamanho} chars)`}
                  </div>
                </FieldCard>
              ))}
            </FieldCardGrid>
          )}

          {/* Botão de Aplicar Mapeamento */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              type="button"
              onClick={handleConfirmApply}
              disabled={campos.length === 0}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                border: 'none',
                color: '#ffffff',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: campos.length === 0 ? 'not-allowed' : 'pointer',
                opacity: campos.length === 0 ? 0.6 : 1,
                boxShadow: '0 4px 14px rgba(2, 132, 199, 0.3)',
                transition: 'all 0.2s',
              }}
            >
              Aplicar Mapeamento de {tipoArquivo.toUpperCase()} ao Distribuidor
            </button>
          </div>
        </div>
      </CenterModalDialog>
    </CenterModalBackdrop>
  )
}

export default ModalLayoutAssistant
